/* eslint-disable */
import _ from 'lodash';
import kdbush from 'kdbush';

/**
 * getParamsFromUrl
 * Returns a key, value object of param name and value.
 */
export function getParamsFromUrl() {
    return _.chain(location.search.slice(1).split('&'))
        .map(item => { if (item) return item.split('='); })
        .compact()
        .fromPairs()
        .value();
}

export function activeSuburbAttribute() {
    const params = getParamsFromUrl();
    return params.surrounding == 'true' ? 'surroundingSuburbs' : 'suburbStateFacet';
}

/**
 * supercluster
 * This is essentially a fork of github.com/mapbox/supercluster
 * Modified to fit in to our context.
 * @param options: {
 *       minZoom: 0,   // min zoom to generate clusters on
 *       maxZoom: 16,  // max zoom level to cluster the points on
 *       radius: 40,   // cluster radius in pixels
 *       extent: 512,  // tile extent (radius is calculated relative to it)
 *       nodeSize: 64, // size of the KD-tree leaf node, affects performance
 * }
 * @return {
 *   load: function (points) This triggers the clustering and stores it in an index.
 *   getClusters: function (zoom) This returns an array of clusters from  the index at specified zoom level.
 * }
 */
export function supercluster(options) {
    return new SuperCluster(options);
}

function SuperCluster(options) {
    this.options = extend(Object.create(this.options), options);
    this.trees = new Array(this.options.maxZoom + 1);
}

SuperCluster.prototype = {
    options: {
        minZoom: 0,   // min zoom to generate clusters on
        maxZoom: 16,  // max zoom level to cluster the points on
        radius: 40,   // cluster radius in pixels
        extent: 512,  // tile extent (radius is calculated relative to it)
        nodeSize: 64, // size of the KD-tree leaf node, affects performance
    },

    load: function (points) {
        var timerId = 'prepare ' + points.length + ' points';

        this.points = points;

        // generate a cluster object for each point
        var clusters = points.map(createPointCluster);

        // cluster points on max zoom, then cluster the results on previous zoom, etc.;
        // results in a cluster hierarchy across zoom levels
        for (var z = this.options.maxZoom; z >= this.options.minZoom; z--) {

            // index input points into a KD-tree
            this.trees[z + 1] = kdbush(clusters, getX, getY, this.options.nodeSize, Float32Array);

            clusters = this._cluster(clusters, z); // create a new set of clusters for the zoom

        }

        // index top-level clusters
        this.trees[this.options.minZoom] = kdbush(clusters, getX, getY, this.options.nodeSize, Float32Array);

        return this;
    },

    getClusters: function (zoom) {
        // reset zoom level if not a number
        const zoomLevel = isNaN(zoom) ? -Infinity : zoom;

        const tree = this.trees[this._limitZoom(zoomLevel)];
        return tree.points.map(cluster => getClusterJSON(cluster));
    },

    _limitZoom: function (z) {
        return Math.max(this.options.minZoom, Math.min(z, this.options.maxZoom + 1));
    },

    _cluster: function (points, zoom) {
        var clusters = [];
        var r = this.options.radius / (this.options.extent * Math.pow(2, zoom));

        // loop through each point
        for (var i = 0; i < points.length; i++) {
            var p = points[i];
            let clusterPoints = p.points;
            // if we've already visited the point at this zoom level, skip it
            if (p.zoom <= zoom) continue;
            p.zoom = zoom;

            // find all nearby points
            var tree = this.trees[zoom + 1];
            var neighborIds = tree.within(p.x, p.y, r);

            var foundNeighbors = false;
            var numPoints = p.numPoints;
            var wx = p.x * numPoints;
            var wy = p.y * numPoints;

            for (var j = 0; j < neighborIds.length; j++) {
                var b = tree.points[neighborIds[j]];
                // filter out neighbors that are too far or already processed
                if (zoom < b.zoom) {
                    foundNeighbors = true;
                    b.zoom = zoom; // save the zoom (so it doesn't get processed twice)
                    wx += b.x * b.numPoints; // accumulate coordinates for calculating weighted center
                    wy += b.y * b.numPoints;
                    numPoints += b.numPoints;
                    clusterPoints = [...clusterPoints, ...b.points]
                }
            }

            clusters.push(foundNeighbors ? createCluster(wx / numPoints, wy / numPoints, clusterPoints) : p);
        }
        return clusters;
    }
};

function createCluster(x, y, points) {
    return {
        x: x, // weighted cluster center
        y: y,
        zoom: Infinity, // the last zoom the cluster was processed at
        points,
        numPoints: points.length,
    };
}

function createPointCluster(p) {
    const coords = [p.location.lng, p.location.lat];
    return createCluster(lngX(coords[0]), latY(coords[1]), [p]);
}

function getClusterJSON(cluster) {
    return {
        numPoints: cluster.numPoints,
        points: cluster.points,
        lng: xLng(cluster.x),
        lat: yLat(cluster.y),
    };
}

// longitude/latitude to spherical mercator in [0..1] range
function lngX(lng) {
    return lng / 360 + 0.5;
}
function latY(lat) {
    var sin = Math.sin(lat * Math.PI / 180),
        y = (0.5 - 0.25 * Math.log((1 + sin) / (1 - sin)) / Math.PI);
    return y < 0 ? 0 :
           y > 1 ? 1 : y;
}

// spherical mercator to longitude/latitude
function xLng(x) {
    return (x - 0.5) * 360;
}
function yLat(y) {
    var y2 = (180 - y * 360) * Math.PI / 180;
    return 360 * Math.atan(Math.exp(y2)) / Math.PI - 90;
}

function extend(dest, src) {
    for (var id in src) dest[id] = src[id];
    return dest;
}

function getX(p) {
    return p.x;
}
function getY(p) {
    return p.y;
}
